import React, { useContext, useState, useEffect } from 'react'
import theme from '@ifca-root/react-component/src/assets/theme'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import { Footer } from '@ifca-root/react-component/src/components/Footer/Footer'
import DynamicSubHeader from '@ifca-root/react-component/src/components/Header/DynamicSubHeader'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { formatDate } from '@ifca-root/react-component/src/helpers/StringNumberFunction/formatDate'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { RejectDialog } from 'components/Dialog/RejectDialog'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useGetUsersByAccountAndSoftwareQuery,
  UserSelection,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { useHistory, useLocation, useParams } from 'react-router'
import { ChangeApportionmentDetailContent } from './ChangeApportionmentDetailContent'
import { Divider, Grid } from '@material-ui/core'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import BusinessIcon from '@material-ui/icons/Business'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import TrendingDownIcon from '@material-ui/icons/TrendingDown'

export const ChangeApportionmentDetail = (props: any) => {
  const history = useHistory()
  const { mode } = props
  const { CompanyID, ChangeApportionmentID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const location = useLocation()
  const locationData = location?.state as any

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [openRejectDialog, setOpenRejectDialog] = useState(false)
  const [rejectDialogInput, setRejectDialogInput] = useState({
    RejectionRemark: '',
  })
  const [clickReject, setIsReject] = useState(false)
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  /* -------------------------------------------- */
  /*                     QUERY                    */
  /* -------------------------------------------- */
  const detailLoading = false
  const data = locationData

  const {
    loading: getUsersLoading,
    data: { getUsersByAccountAndSoftware } = {
      getUsersByAccountAndSoftware: [],
    },
  } = useGetUsersByAccountAndSoftwareQuery({
    fetchPolicy: 'network-only',
    variables: { superUserBool: UserSelection.All },
  })

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const approveRejectLoading = false

  const handleApprove = () => {
    setOpenSnackBar(true)
    setSnackBarMsg(SystemMsgs.postNewRecord())
    history.push(
      `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
    )
  }

  const handleReject = () => {
    setOpenSnackBar(true)
    setSnackBarMsg(SystemMsgs.rejectNewRecord())
    history.push(
      `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
    )
  }

  useEffect(() => {
    if (clickReject) {
      handleReject()
      setIsReject(false)
    }
  }, [clickReject])

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Draft'
      case 'SUBMIT':
        return 'Submitted'
      case 'COMPLETED':
        return 'Approved'
      case 'REJECTED':
        return 'Rejected'
      default:
        return status
    }
  }

  const getTotalPercentageChange = () => {
    if (!data?.Details || data.Details.length === 0) return 0
    return data.Details.reduce((sum: number, d: any) => {
      const diff = Math.abs((d.new_percentage || 0) - (d.old_percentage || 0))
      return sum + diff
    }, 0)
  }

  const isLoading = detailLoading || getUsersLoading || approveRejectLoading

  return (
    <>
      {isLoading && <Loading />}

      <MainHeader
        mainBtn="back"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/change-apportionment`
          )
        }}
        smTitle="Fixed Assets"
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Change Apportionment (%)', current: true },
        ]}
        rightRouteSegments={[
          { name: getStatusLabel(data?.ApprovalStatus), current: true },
        ]}
      />

      {data && (
        <DynamicSubHeader
          title={
            <span className="c-orange">
              <span style={{ fontWeight: 'bold' }}>{data?.Asset?.name}</span>
            </span>
          }
          rightText={
            <span className="c-orange">
              <span style={{ fontWeight: 'bold' }}>
                {data?.Asset?.asset_card_no}
              </span>
            </span>
          }
          infoLine={
            <>
              <div className="text-overflow" style={{ paddingBottom: '4px' }}>
                <span>{data?.DocNo || 'Draft'}</span>
              </div>
              <div className="text-overflow">
                Departments: <span>{data?.Details?.length || 0}</span> |{' '}
                <span style={{ color: 'orange' }}>
                  {getTotalPercentageChange().toFixed(2)}% changed
                </span>
              </div>
            </>
          }
          rightInfoLine={
            <span>{data?.change_date ? formatDate(data.change_date) : ''}</span>
          }
        />
      )}

      <ContentWrapper multiDynamicInfo float footer>
        <CardContents>
          <ChangeApportionmentDetailContent
            listEl={data}
            listStatus={data?.ApprovalStatus}
            userList={getUsersByAccountAndSoftware}
            mode={'detail'}
          />
        </CardContents>

        {/* Department Allocation Details */}
        {data?.Details && data.Details.length > 0 && (
          <CardContents
            section={{
              header: { title: 'Department Allocation Changes' },
            }}
          >
            <div className="rm-padding table-wrap">
              {data.Details.map((detail: any, index: number) => {
                const oldPercentage = detail.old_percentage || 0
                const newPercentage = detail.new_percentage || 0
                const percentageChange = newPercentage - oldPercentage
                const hasChange = Math.abs(percentageChange) > 0.01

                const getChangeColor = (oldVal: number, newVal: number) => {
                  if (newVal > oldVal) return '#000'
                  if (newVal < oldVal) return '#000'
                  return '#666'
                }

                const getChangeIcon = (oldVal: number, newVal: number) => {
                  if (newVal > oldVal)
                    return (
                      <TrendingUpIcon
                        style={{ fontSize: 16, color: 'green' }}
                      />
                    )
                  if (newVal < oldVal)
                    return (
                      <TrendingDownIcon
                        style={{ fontSize: 16, color: 'red' }}
                      />
                    )
                  return null
                }

                return (
                  <React.Fragment key={index}>
                    <Grid spacing={1} container className="table-content">
                      <Grid item xs={1} style={{ placeSelf: 'start' }}>
                        <span className="xxTitle">{`${index + 1}.`}</span>
                      </Grid>

                      <Grid
                        item
                        xs={8}
                        style={{
                          placeSelf: 'start',
                          marginTop: '8px',
                        }}
                      >
                        {/* Department */}
                        <div
                          className="xsTitle text-noflow"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <BusinessIcon
                            style={{
                              fontSize: 16,
                              marginRight: 6,
                              color: theme.palette.primary.main,
                            }}
                          />
                          {detail.Department?.Code ||
                            detail.Department?.Name ||
                            'N/A'}
                        </div>

                        {/* Department Name */}
                        {detail.Department?.Code && detail.Department?.Name && (
                          <div
                            className="desc text-noflow"
                            style={{ marginLeft: '22px' }}
                          >
                            {detail.Department?.Name}
                          </div>
                        )}

                        {/* Location */}
                        {detail.Location?.name && (
                          <div
                            className="desc text-noflow"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginTop: '4px',
                            }}
                          >
                            <LocationOnIcon
                              style={{
                                fontSize: 14,
                                marginRight: 6,
                                color: '#F39C12',
                              }}
                            />
                            {detail.Location?.name}
                          </div>
                        )}

                        {/* Percentage Info */}
                        <div style={{ marginTop: '8px', marginLeft: '22px' }}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <div style={{ fontSize: '10px', color: '#999' }}>
                                {`Alloc. (%)`}
                              </div>
                              <div style={{ fontSize: '12px' }}>
                                {oldPercentage.toFixed(2)}%
                              </div>
                            </Grid>
                            <Grid item xs={6}>
                              <div style={{ fontSize: '10px', color: '#999' }}>
                                {`Changed Alloc. (%)`}
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  // fontWeight: 'bold',
                                  color: hasChange
                                    ? getChangeColor(
                                        oldPercentage,
                                        newPercentage
                                      )
                                    : '#000',
                                }}
                              >
                                {newPercentage.toFixed(2)}%
                              </div>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                        style={{
                          placeSelf: 'start',
                          marginTop: '8px',
                          textAlign: 'right',
                        }}
                      >
                        <div style={{ fontSize: '10px', color: '#999' }}>
                          Qty
                        </div>
                        <div className="xxTitle">
                          {detail.new_quantity?.toFixed(2) || '0.00'}
                        </div>
                      </Grid>
                    </Grid>

                    <Divider
                      variant="fullWidth"
                      style={{ background: '#E4E4E4' }}
                    />
                  </React.Fragment>
                )
              })}
            </div>
          </CardContents>
        )}
      </ContentWrapper>

      {mode === 'workdesk' && data?.ApprovalStatus === 'SUBMIT' && (
        <Footer
          options={[
            {
              name: 'Reject',
              onClick: () => setOpenRejectDialog(true),
              color: 'secondary',
            },
            {
              name: 'Approve',
              onClick: handleApprove,
              color: 'primary',
            },
          ]}
        />
      )}

      {data?.ApprovalStatus === 'SUBMIT' && mode !== 'workdesk' && (
        <Footer
          options={[
            {
              name: 'Reject',
              onClick: () => setOpenRejectDialog(true),
              color: 'secondary',
            },
            {
              name: 'Approve',
              onClick: handleApprove,
              color: 'primary',
            },
          ]}
        />
      )}

      <RejectDialog
        data={data}
        openRejectDialog={openRejectDialog}
        setOpenRejectDialog={setOpenRejectDialog}
        setRejectDialogInput={setRejectDialogInput}
        setIsReject={setIsReject}
      />

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error'}
      />
    </>
  )
}
